import {
  Components,
  PaletteOptions,
  SimplePaletteColorOptions,
  Theme,
  createTheme,
  filledInputClasses,
  inputLabelClasses,
  paperClasses,
  tableCellClasses,
} from '@mui/material';
import { ColorPartial } from '@mui/material/styles/createPalette';

// Used only to create transitions
const muiTheme = createTheme();

export function createComponents(palette: PaletteOptions): Components<Omit<Theme, 'components'>> {
  const primaryColor = palette.primary as SimplePaletteColorOptions;
  const errorColor = palette.error as SimplePaletteColorOptions;
  const greyColor = palette.grey as ColorPartial;

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          // Add a transparent border to avoid resizing the button on hover
          border: '1px solid transparent',
          '&.Mui-disabled': {
            '& .MuiButton-startIcon svg': {
              fill: greyColor[500], // Set the color for the startIcon when disabled
            },
          },
        },
        outlined: {
          '&:hover': {
            backgroundColor: palette?.secondary_component_hover,
          },
        },
        text: {
          '&:hover': {
            border: '1px solid', // Add a border
            borderColor: palette?.secondary_component_hover,
          },
        },
        sizeSmall: {
          padding: '6px 16px',
        },
        sizeMedium: {
          padding: '8px 20px',
        },
        sizeLarge: {
          padding: '11px 24px',
        },
        textSizeSmall: {
          padding: '7px 12px',
        },
        textSizeMedium: {
          padding: '9px 16px',
        },
        textSizeLarge: {
          padding: '12px 16px',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fill: primaryColor.main,
          '&.Mui-disabled': {
            fill: '#9e9e9e',
          },
          width: 30,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          [`&.${paperClasses.elevation1}`]: {
            boxShadow:
              '0px 5px 22px rgba(0, 0, 0, 0.04), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.03)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px 24px',
          '&:last-child': {
            paddingBottom: '32px',
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
        subheaderTypographyProps: {
          variant: 'body2',
        },
      },
      styleOverrides: {
        root: {
          padding: '32px 24px 16px',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        '#__next': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        },
        '#nprogress': {
          pointerEvents: 'none',
        },
        '#nprogress .bar': {
          backgroundColor: primaryColor.main,
          height: 3,
          left: 0,
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 2000,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
          },
        },
        root: {
          '&.Mui-disabled .MuiSvgIcon-root': {
            fill: greyColor[500],
            opacity: 0.6,
          },
          '&.Mui-disabled': {
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '45px',
          '&::placeholder': {
            color: palette.text?.secondary,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: 8,
          borderStyle: 'solid',
          borderWidth: 1,
          overflow: 'hidden',
          borderColor: palette?.dialog_title,
          opacity: 0.6,
          transition: muiTheme.transitions.create([
            'border-color',
            'box-shadow',
          ]),
          '&:before': {
            display: 'none',
          },
          '&:after': {
            display: 'none',
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: 'transparent',
            fill: greyColor[700],
            opacity: 0.6,
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: 'transparent',
            borderColor: primaryColor.main,
            boxShadow: `${primaryColor.main} 0 0 0 2px`,
          },
          [`&.${filledInputClasses.error}`]: {
            borderColor: errorColor.main,
            boxShadow: `${errorColor.main} 0 0 0 2px`,
          },
        },
        input: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '24px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: palette.action?.hover,
          },
        },
        input: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '24px',
          '&.Mui-disabled': {
            color: palette.disabled_color,
            WebkitTextFillColor: palette.disabled_color,
          },
        },
        notchedOutline: {
          borderColor: palette?.dialog_title,
          opacity: 0.6,
          transition: muiTheme.transitions.create([
            'border-color',
            'box-shadow',
          ]),
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          padding: '0px 3px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500,
          [`&.${inputLabelClasses.filled}`]: {
            transform: 'translate(12px, 18px) scale(1)',
          },
          [`&.${inputLabelClasses.shrink}`]: {
            [`&.${inputLabelClasses.standard}`]: {
              transform: 'translate(0, -1.5px) scale(0.85)',
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: 'translate(12px, 6px) scale(0.85)',
            },
            [`&.${inputLabelClasses.outlined}`]: {
              transform: 'translate(14px, -9px) scale(0.85)',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.71,
          minWidth: 'auto',
          paddingLeft: 0,
          paddingRight: 0,
          textTransform: 'none',
          '& + &': {
            marginLeft: 24,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: palette.divider,
          padding: '15px 16px',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
            backgroundColor: palette?.data_table_header,
            color: palette?.data_header_text,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          },
          [`& .${tableCellClasses.paddingCheckbox}`]: {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            border: `1px solid ${palette?.primary_component_border}`,
            color: palette?.primary_component_border,
          },
          '& .MuiInputBase-input': {
            color: palette?.dialog_text,
            '&::placeholder': {
              color: palette?.dialog_text,
              opacity: 1,
            },
          },
          '& .MuiInputLabel-root': {
            position: 'absolute',
            backgroundColor: palette?.dialog_background,
          },
        },
        paper: {
          backgroundColor: palette?.dialog_background,
          backgroundImage: 'none !important',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: palette?.dialog_title, // Set your desired text color
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: palette?.dialog_background,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: palette?.dialog_background,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette?.tooltip_color,
          color: palette?.text_color,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-disabled .MuiSvgIcon-root': {
            fill: greyColor[500],
            opacity: 0.6,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: palette?.accordion_color,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // Apply Dialog-like field styles to all inputs inside Backdrop modals
          '& .MuiInputBase-root': {
            border: `1px solid ${palette?.primary_component_border}`,
            color: palette?.primary_component_border,
          },
          '& .MuiInputBase-input': {
            color: palette?.dialog_text,
            '&::placeholder': {
              color: palette?.dialog_text,
              opacity: 1,
            },
          },
          '& .MuiInputLabel-root': {
            position: 'absolute',
            backgroundColor: palette?.dialog_background,
          },
        },
      },
    },
  };
}
